import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, NgControl, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CacheService } from 'src/app/services/cache.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { NotificationType } from 'src/app/enum/NotificationType';
import { StorageKey, StorageType } from 'src/app/storage-key';
import { StorageService } from 'src/app/services/storage.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.css']
})
export class ChangePasswordComponent implements OnInit {
  changePasswordForm!: FormGroup;
  isOldPawordValid: boolean = true;
  UserId: any;
  isCPBtnDisable: boolean = false;
  hasPassword: boolean = true;
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  constructor(formBuilder: FormBuilder,
    public userService: UserService,
    private cacheService: CacheService,
    private modalService: NgbModal,
    private notificationService: NotificationService,
    private storageService: StorageService,
    private authService: AuthService
  ) {
    this.changePasswordForm = formBuilder.group({
      'oldPwd': [''],
      'newPwd': ['', Validators.required],
      'confirmPwd': ['', Validators.required]
    },
      { validator: this.passwordMatchValidator }
    );
  }
  ngOnInit() {
    this.UserId = this.authService.getUserId() || 0;

    if (this.UserId) {
      this.getUserDetailsById(this.UserId);
    }
  }
  passwordMatchValidator(group: AbstractControl) {
    const newPassword = group.get('newPwd')?.value;
    const confirmPassword = group.get('confirmPwd')?.value;
    return newPassword === confirmPassword ? null : { passwordsNotMatching: true };
  }
  get oldPwd() {
    return this.changePasswordForm.get('oldPwd');
  }

  get newPwd() {
    return this.changePasswordForm.get('newPwd');
  }

  get confirmPwd() {
    return this.changePasswordForm.get('confirmPwd');
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  get passwordStrengthScore(): number {
    const password = String(this.newPwd?.value ?? '');

    if (!password) {
      return 0;
    }

    let score = 0;

    if (password.length >= 8) {
      score += 1;
    }
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
      score += 1;
    }
    if (/\d/.test(password)) {
      score += 1;
    }
    if (/[^A-Za-z0-9]/.test(password)) {
      score += 1;
    }

    return score;
  }

  get passwordStrengthLabel(): string {
    const score = this.passwordStrengthScore;

    if (!this.newPwd?.value) {
      return '';
    }

    if (score <= 1) {
      return 'Weak password';
    }

    if (score === 2) {
      return 'Fair password';
    }

    if (score === 3) {
      return 'Good password';
    }

    return 'Strong password';
  }

  get passwordStrengthMessage(): string {
    const score = this.passwordStrengthScore;

    if (!this.newPwd?.value) {
      return '';
    }

    if (score < 4) {
      return 'Strong password recommended';
    }

    return 'Strong password';
  }

  getUserDetailsById(id: number) {
    this.userService.getUserFromID(id).subscribe((res) => {
      if (res) {
        this.hasPassword = res.password != null;

        this.storageService.setItem(StorageKey.IS_LOGGED_IN, 'true', StorageType.LOCAL);
        this.storageService.setItem(StorageKey.USER, JSON.stringify(res), StorageType.LOCAL);

        this.cacheService.onUserDetailsChange(res);

        this.UserId = res.id;

        const oldPwdControl = this.changePasswordForm.get('oldPwd');
        if (oldPwdControl) {
          if (this.hasPassword) {
            oldPwdControl.setValidators(Validators.required);
          } else {
            oldPwdControl.clearValidators();
            oldPwdControl.setValue('');
          }
          oldPwdControl.updateValueAndValidity({ emitEvent: false });
        }
      }
    });
  }

  toggleAlert() {
    this.isOldPawordValid = true;
  }

  ChangePassword() {
    this.changePasswordForm.markAllAsTouched();

    if (this.changePasswordForm.invalid) {
      return;
    }

    this.isCPBtnDisable = true;
    this.userService.getUserFromID(this.UserId).subscribe((res) => {
      if (!res) {
        this.isCPBtnDisable = false;
        this.notificationService.notifyMessage(NotificationType.Warning, `Something went wrong!`);
        return;
      }

      const currentPassword = res?.password ?? '';
      const enteredOldPassword = this.changePasswordForm.controls['oldPwd'].value;
      const newPassword = this.changePasswordForm.controls['newPwd'].value;

      if (this.hasPassword && enteredOldPassword !== currentPassword) {
        this.isOldPawordValid = false;
        this.isCPBtnDisable = false;
        return;
      }

      if (currentPassword && currentPassword === newPassword) {
        this.isCPBtnDisable = false;
        this.notificationService.notifyMessage(NotificationType.Warning, 'New password must be different from the current password.');
        return;
      }

      this.userService.changePassword(this.UserId, newPassword).subscribe((changeRes) => {
        if (changeRes) {
          this.getUserDetailsById(this.UserId);
          this.closeModal();
          this.notificationService.notifyMessage(NotificationType.Success, `You're password updated successfully!`);
        } else {
          this.notificationService.notifyMessage(NotificationType.Warning, `Something went wrong!`);
        }
        this.isCPBtnDisable = false;
      }, () => {
        this.isCPBtnDisable = false;
        this.notificationService.notifyMessage(NotificationType.Warning, `Something went wrong!`);
      });
    }, () => {
      this.isCPBtnDisable = false;
      this.notificationService.notifyMessage(NotificationType.Warning, `Something went wrong!`);
    });
  }

}
